import { useState } from "react";
import { Twitter, Instagram, Youtube, Linkedin, Github, ArrowRight, Loader2 } from "lucide-react";
import { postNewsletter } from "../api";
import { useSiteContent } from "../contexts/SiteContent";

const SOCIAL = [
  { icon: Twitter, label: "X", key: "social.twitter_url" },
  { icon: Instagram, label: "Instagram", key: "social.instagram_url" },
  { icon: Youtube, label: "YouTube", key: "social.youtube_url" },
  { icon: Linkedin, label: "LinkedIn", key: "social.linkedin_url" },
  { icon: Github, label: "GitHub", key: "social.github_url" },
];

export default function SocialNewsletter() {
  const { t } = useSiteContent();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    if (!email || !email.includes("@")) {
      setStatus({ ok: false, kind: "invalid", message: "Please enter a valid email address." });
      return;
    }
    setLoading(true);
    try {
      const { data } = await postNewsletter(email);
      if (data?.already_subscribed) {
        setStatus({ ok: true, kind: "already", message: "You're already on the list — thanks for sticking with us." });
      } else {
        setStatus({ ok: true, kind: "new", message: "Thanks! You're subscribed. Watch your inbox." });
      }
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) && detail[0]?.msg
        ? detail[0].msg
        : "Couldn't subscribe. Please check your email and try again.";
      setStatus({ ok: false, kind: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="social-newsletter" className="bg-[#f6f7f8] py-16 md:py-20">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-start">
          <div>
            <p className="text-[13px] uppercase tracking-[0.18em] font-medium text-neutral-600">
              {t("social.eyebrow", "Follow us")}
            </p>
            <h3 className="mt-3 text-[22px] md:text-[26px] font-semibold tracking-tight text-neutral-900">
              {t("social.heading", "Stay close to the work")}
            </h3>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              {SOCIAL.map(({ icon: Icon, label, key }) => (
                <a
                  key={label}
                  href={t(key, "#")}
                  data-testid={`social-${label.toLowerCase()}`}
                  aria-label={label}
                  className="grid place-items-center w-11 h-11 rounded-full bg-white border border-neutral-200 text-neutral-700 hover:text-neutral-900 hover:border-neutral-400 transition-colors"
                >
                  <Icon className="h-4 w-4" strokeWidth={2} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[13px] uppercase tracking-[0.18em] font-medium text-neutral-600">
              {t("newsletter.eyebrow", "Newsletter")}
            </p>
            <h3 className="mt-3 text-[22px] md:text-[26px] font-semibold tracking-tight text-neutral-900">
              {t("newsletter.heading", "Get the latest from our research teams")}
            </h3>
            <form
              data-testid="newsletter-form"
              onSubmit={onSubmit}
              className="mt-5 flex flex-col sm:flex-row gap-3"
              noValidate
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("newsletter.placeholder", "you@domain.com")}
                data-testid="newsletter-email-input"
                className="flex-1 h-11 rounded-full px-5 bg-white border border-neutral-300 text-[14px] text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-900/10 focus:border-neutral-900"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-neutral-900 px-6 h-11 text-[13px] font-medium text-white transition-all hover:bg-neutral-800 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t("newsletter.cta_label", "Get the latest updates")}
                {!loading && <ArrowRight className="h-3.5 w-3.5" />}
              </button>
            </form>
            {status && (
              <p
                data-testid={`newsletter-status-${status.kind}`}
                className={`mt-3 text-[13px] ${status.ok ? "text-emerald-700" : "text-rose-700"}`}
              >
                {status.message}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
